/** Atlas key derivation for the battle screen.
 *
 * Content defs carry `art` as a filesystem path; the atlas is keyed
 * semantically. Nothing in the shipped bundle maps one to the other, so the
 * mapping lives here, driven off `def.art` rather than off the id - `art` is the
 * field that actually tracks the sprite, and six neutral creatures prove it:
 * their art points into the map-object set and they have no battle sheet at all.
 *
 * Pure string work. No renderer, no DOM.
 */

/** The six columns of a unit sheet row, which are six authored poses. */
export const POSE = {
  /** column 0 - the resting stance every sheet opens with. */
  idle: 0,
  /** column 1 - weapon readied / guard. Used as the second walk frame. */
  ready: 1,
  /** column 2 - the primary forward attack. */
  attack: 2,
  /** column 3 - the follow-through, and the shooter's release. */
  attackAlt: 3,
  /** column 4 - the big one: charge, volley, wing-sweep. Used for shooting. */
  special: 4,
  /** column 5 - crouch / kneel / brace. Used for DEFEND and for taking a hit. */
  defend: 5,
} as const;

export type PoseName = keyof typeof POSE;

/**
 * The pose a living stack stands in when nothing is happening, and why it is
 * not column 0.
 *
 * Column 0 is the frame every sheet opens with, and every sheet opens with the
 * same problem: the creature is square to the camera. Measured as the mirror
 * IoU of the top 45% of the silhouette's alpha - 1.0 is perfectly bilateral,
 * i.e. facing the viewer dead on - over the 77 creatures whose shipped the classic
 * `Standing` sprites could be paired with one of ours, the six columns come out
 *
 *   front.0  0.635    front.1  0.591    front.2  0.477
 *   front.3  0.498    front.4  0.548    front.5  0.594
 *
 * against the classic's own resting frames at a median of **0.549**. Ours was more
 * camera-square than the reference median in 49 of the 77 on column 0 and in 41
 * on column 1, and the battle screen was resting on the single most symmetric
 * column in the sheet. The turned, enemy-facing frames were already drawn and
 * already packed.
 *
 * (`sprite-critic` reports the same six in the same order at a different offset
 * - 0.714 / 0.609 / 0.512 / 0.537 / 0.594 / 0.646 against a reference of 0.586 -
 * from a different bounding-box convention. The numbers above are the ones
 * measured here, on the shipped atlas, and the two agree on everything the
 * choice turns on: column 0 is the most bilateral of the six, column 1 is the
 * closest to the reference among the poses that are a *stance*, and the
 * reference sits below both.)
 *
 * This also fixes a mirror that was doing nothing. `stacks.ts` faces a stack by
 * flipping `scale.x`, and flipping a bilaterally symmetric sprite is a no-op,
 * so neither army was looking at the other however the facing was set.
 *
 * (The colour channels say the opposite and are the wrong channels: on RGB the
 * two rosters are equally symmetric, 0.490 ours against 0.460 theirs. The
 * silhouette is what a pose reads as at 60px; the shading inside it is not.)
 *
 * Column 1 - the readied guard - is the closest to the reference of the columns
 * that are a *stance* rather than an action. Column 4 is nearer still (0.548
 * against 0.549) and column 5 ties column 1, but 4 is the volley/charge frame
 * the shooter needs and 5 is a crouch; a creature cannot spend a battle
 * kneeling or mid-swing. The idle loop in `stacks.ts` beats between this and
 * column 0, which is now the shift rather than the rest.
 */
export const RESTING_POSE: PoseName = "ready";

/**
 * The pose a melee attacker coils into before it strikes.
 *
 * It was `ready`, which was a different frame from the rest pose when the rest
 * pose was `idle` and is the same frame now. A wind-up that does not change the
 * frame is not a wind-up, so it is the crouch: `defend` is the sheet's braced
 * column, which is what a body does before it throws its weight forward. The
 * struck stack uses the same column at a different moment, on a different
 * stack; the two never collide.
 */
export const MELEE_WINDUP_POSE: PoseName = "defend";

/** Which row of the sheet: the three-quarter front view, or the matching rear. */
export type UnitView = "front" | "rear";

export interface UnitSheetRef {
  readonly kind: "sheet";
  readonly faction: string;
  readonly unit: string;
}

export interface SingleSpriteRef {
  readonly kind: "single";
  readonly key: string;
}

export type CreatureArtRef = UnitSheetRef | SingleSpriteRef;

const stripExt = (s: string): string => (s.endsWith(".png") ? s.slice(0, -4) : s);

/**
 * `sunspire/01_spearwarden_glaivewarden.png` -> the twelve-frame sheet
 * `sunspire.spearwarden_glaivewarden.*`; a base creature and its upgrade share one
 * sheet, which is why the unit segment names both.
 *
 * `TILES/assets/objects/monster/mon_bandit.png` -> the single map-object sprite
 * `obj.mon_bandit`. Those six neutrals have adventure art and nothing else.
 */
export function creatureArtRef(art: string): CreatureArtRef {
  const parts = stripExt(art).split("/");
  const base = parts[parts.length - 1] ?? "";
  if (parts.length >= 2 && parts[0] === "TILES") return { kind: "single", key: `obj.${base}` };
  // A war machine has no unit sheet: it is one frame, packed beside the
  // artifact that fields it. `WAR_MACHINE_KEYS` below names the same five.
  if (parts.length >= 2 && parts[0] === "BATTLE") {
    return { kind: "single", key: `warmachine.${base.replace(/^wm_/, "")}` };
  }
  const faction = parts.length >= 2 ? parts[parts.length - 2]! : "";
  const unit = base.replace(/^\d+_/, "");
  if (faction === "" || unit === "") return { kind: "single", key: `obj.${base}` };
  return { kind: "sheet", faction, unit };
}

/** A single frame of a unit sheet. */
export function unitFrameKey(ref: UnitSheetRef, view: UnitView, pose: number): string {
  return `${ref.faction}.${ref.unit}.${view}.${pose}`;
}

/**
 * The painted siege masonry: windows of one oblique-view wall run plus the
 * gatehouse, turret, keep and the far top run, packed from
 * `BATTLE/assets/machines/wm_siege_*.png` through the war-machine path (the
 * one keyed-sprite path under BATTLE/). `arena.ts` stamps these when the
 * atlas has them and falls back to its procedural masonry when it does not.
 */
/**
 * `turret` is the cone-capped drum that closes the top run at the frame;
 * `drum` is the open crenellated tower that stands ON the curtain, which is
 * what the classic's castle carries along its wall (a reference screenshot x480-560:
 * a ring of merlons with the fighting platform inside it, no roof).
 */
export const SIEGE_PIECES = [
  "bay_0", "bay_1", "bay_2", "bay_3", "bay_4", "bay_damaged", "bay_breach",
  "gate", "gate_open", "turret", "drum", "keep_top", "keep_bottom", "toprun",
] as const;
/**
 * The keep ships in two halves stacked by `arena.ts:buildKeep`: one piece is
 * capped at 228 texels a side by the hero doll's socket rule
 * (`packages/ui/test/hero-plate.spec.ts`), and the classic's keep is seven row
 * pitches tall (`build_siege.py` KEEP_TEXELS).
 */
export const KEEP_HALVES = ["keep_top", "keep_bottom"] as const;
/** How many consecutive curtain windows the sheet carries; see `build_siege.py`. */
export const SIEGE_BAY_WINDOWS = 5;
export type SiegePiece = (typeof SIEGE_PIECES)[number];

export function siegeKey(piece: SiegePiece): string {
  return `warmachine.siege_${piece}`;
}

/** The battlefield backdrop for a terrain name, falling back to open grass. */
export function battlefieldKey(terrain: string): string {
  const t = battlefieldId(terrain);
  return BATTLEFIELDS.has(t) ? `battlefield.${t}` : `battlefield.${BATTLEFIELD_ALIAS[t] ?? "grass"}`;
}

/**
 * Accept a TerrainKind (`lava`), an atlas id, or the content art path
 * (`BATTLE/assets/battlefields/bf_lava.png`). The kernel now stores the kind;
 * the path form is kept so a stale save or a test that still passes a file
 * does not silently draw grass.
 */
export function battlefieldId(terrain: string): string {
  let t = terrain.toLowerCase();
  const slash = t.lastIndexOf("/");
  if (slash >= 0) t = t.slice(slash + 1);
  const dot = t.lastIndexOf(".");
  if (dot > 0) t = t.slice(0, dot);
  if (t.startsWith("bf_")) t = t.slice(3);
  return t;
}

/**
 * The twenty-one painted arenas, one per `BATTLE/assets/battlefields/bf_*.png`.
 *
 * `highlands` and `wasteland` were aliased away - to `rough` and `stone_barrens` -
 * while their own plates were packed and shipping. Both are declared terrain
 * kinds (`packages/content/src/testdata.ts` gives highlands
 * `battlefield: "battle/highlands"`) and both are in the mapgen biome tables, so
 * a battle fought on either drew a different arena from the one the map said it
 * was on. They are not near misses either: `bf_highlands.png` is green moorland
 * with heather and grey outcrops (plate mid `0x6b7342`) against `bf_rough.png`'s
 * orange cracked earth (`0xac8354`), and `bf_wasteland.png` is grey-green cracked
 * mud under a violet sky (`0x736b52`) against `bf_stone_barrens.png`'s pale grey
 * hardpan (`0x888480`).
 */
const BATTLEFIELDS = new Set([
  "cavern", "lucky_meadow", "hexed_soil", "dirt", "gloom_mist", "valor_fields",
  "ember_fields", "grass", "highlands", "blessed_earth", "lava", "mirror_pools",
  "spell_clouds", "ley_plains", "stone_barrens", "rough", "sand", "snow", "swamp",
  "wasteland", "water",
]);

/** TerrainKind ids and a few common synonyms onto the twenty-one painted arenas. */
const BATTLEFIELD_ALIAS: Readonly<Record<string, string>> = {
  grassland: "grass", subterranean: "cavern", underground: "cavern",
  desert: "sand", water_terrain: "water", ocean: "water", road: "dirt",
};

/**
 * Props per arena, split by what they are allowed to mean.
 *
 * `cover` is a **blocking obstacle**: a stack may not enter its cell, and a
 * player has to be able to read that off the sprite. `litter` is ground dress -
 * a molehill, a handful of pebbles, a patch of clover - which is right scattered
 * flat across the board and wrong as the thing that blocks a charge.
 *
 * The two used to be one list, and `obstacleKey` picked over all of it: on dirt
 * a third of the blocking cells came back as pebbles or a signpost. `arena.ts`
 * worked round it with a deny-list of its own (`LITTER` / `coverKey`), which is
 * the same knowledge written twice and in the wrong file - the palette is the
 * only place that knows what each prop *is*. That deny-list can go.
 *
 * `obs_hedgerow` is litter for the other reason: it is a saturated green box,
 * and three of them on one arena read as garden furniture rather than terrain.
 *
 * The kernel's `Obstacle.art` is authoritative when it names a real key; when it
 * is empty a stable pick from `cover` keeps a lava field from sprouting birch
 * trees. The pick is by hex index, so the same battle always draws the same rock
 * in the same place.
 *
 * A `litter` list of ONE is a list of none. `scatterGroundProps` dresses roughly
 * one cell in six, which is a dozen or more pieces on a full board, so a
 * single-entry list draws the same pebble cluster a dozen times in one frame -
 * measured on the shipped cavern shot, where `obs_glow_moss` was the whole of
 * that arena's dress. Nine arenas shipped with one litter key and one - cursed
 * ground - with none at all, which is why `arena.ts` carried a fallback into
 * `cover` for it. Every one of the sprites added here was already packed and
 * reachable from no arena, and none of them is any arena's `cover`, which the
 * "a sprite any OTHER arena blocks a cell with" assertion in
 * `battle-arena.spec.ts` holds.
 */
interface Palette {
  readonly cover: readonly string[];
  readonly litter: readonly string[];
}

const OBSTACLE_PALETTE: Readonly<Record<string, Palette>> = {
  grass: {
    cover: ["obs_oak", "obs_birch", "obs_rocks", "obs_bush", "obs_stump", "obs_boulder"],
    litter: ["obs_flowers", "obs_hedgerow", "obs_molehill", "obs_tall_grass"],
  },
  lucky_meadow: {
    cover: ["obs_hazel_bush", "obs_wildrose", "obs_apple_tree"],
    litter: ["obs_clover_patch", "obs_flower_ring", "obs_molehill"],
  },
  valor_fields: {
    cover: ["obs_broken_cart", "obs_bones", "obs_fence", "obs_watchfire", "obs_woodpile"],
    litter: ["obs_milestone", "obs_flowers", "obs_tall_grass"],
  },
  dirt: {
    cover: ["obs_deadtree", "obs_rubble_pile", "obs_rocks", "obs_dry_well"],
    litter: ["obs_signpost", "obs_pebbles", "obs_dig_hole", "obs_molehill", "obs_anthill"],
  },
  rough: {
    cover: ["obs_mesa_rock", "obs_boulder", "obs_deadbush", "obs_rock_arch", "obs_ruined_column"],
    litter: ["obs_scree", "obs_dig_hole", "obs_fallen_branch", "obs_salt_crust"],
  },
  // Upland moor, not a second `rough`: the plate is heather and turf over grey
  // outcrops. Every sprite here was already packed and drawn by no arena, which
  // is what an alias costs - `obs_moor_cairn`, `obs_standing_stone`,
  // `obs_thorn_hedge`, `obs_heather_patch` and `obs_peat_mound` exist for this
  // one board and had never been reachable.
  highlands: {
    cover: ["obs_moor_cairn", "obs_standing_stone", "obs_boulder", "obs_thorn_hedge", "obs_rocks"],
    litter: ["obs_heather_patch", "obs_peat_mound", "obs_tall_grass"],
  },
  // Poisoned ground: slag, rust and tar, which is what its plate paints. Sharing
  // `obs_deadtree` with dirt and cursed ground is deliberate - a dead tree is a
  // dead tree - and the rest are this arena's own.
  wasteland: {
    cover: ["obs_slag_heap", "obs_rusted_boiler", "obs_ore_vein", "obs_rotting_fence", "obs_deadtree"],
    litter: ["obs_tar_seep", "obs_dry_riverbed", "obs_sulfur_crust", "obs_ash_drift"],
  },
  stone_barrens: {
    cover: ["obs_desert_rock", "obs_sandstone_pillar", "obs_boulder", "obs_ruined_arch", "obs_mesa_rock"],
    litter: ["obs_scree", "obs_salt_crust", "obs_dig_hole", "obs_sand_ripple"],
  },
  sand: {
    cover: ["obs_cactus", "obs_camel_bones", "obs_palm", "obs_desert_bones"],
    litter: ["obs_dune", "obs_tumbleweed", "obs_sand_ripple", "obs_salt_crust"],
  },
  snow: {
    cover: ["obs_snowpine", "obs_snow_boulder", "obs_ice_spikes", "obs_iceblock", "obs_snow_stump"],
    litter: ["obs_snow_drift", "obs_snow_hummock", "obs_glacier_crack"],
  },
  swamp: {
    cover: ["obs_swamp_log", "obs_reeds", "obs_bog_pool", "obs_mangrove_root", "obs_toadstool_ring"],
    litter: ["obs_marsh_grass", "obs_bog_bubbles", "obs_dead_reed"],
  },
  lava: {
    cover: ["obs_lavarock", "obs_lava_spire", "obs_cinder_cone", "obs_scorched_tree", "obs_obsidian_shard"],
    litter: ["obs_ember_pile", "obs_lavacrack", "obs_sulfur_crust", "obs_ash_drift"],
  },
  ember_fields: {
    cover: ["obs_lava_pool", "obs_burnt_stump", "obs_gas_vent", "obs_smoking_vent"],
    litter: ["obs_magma_crack", "obs_ash_mound", "obs_sulfur_crust"],
  },
  cavern: {
    cover: ["obs_stalagmite", "obs_cave_column", "obs_cave_mushroom", "obs_stalactite", "obs_moss_rock"],
    litter: ["obs_glow_moss", "obs_cave_pool", "obs_sulfur_crust", "obs_dig_hole", "obs_fallen_branch"],
  },
  hexed_soil: {
    cover: ["obs_gravestones", "obs_grave_cross", "obs_bone_pile", "obs_deadtree", "obs_ruined_wall", "obs_broken_statue"],
    litter: ["obs_dig_hole", "obs_fallen_branch", "obs_nettle_patch"],
  },
  gloom_mist: {
    cover: ["obs_deadfall", "obs_bat_roost", "obs_ivy_trunk", "obs_shelf_fungus", "obs_hollow_log"],
    litter: ["obs_spider_web", "obs_fallen_branch", "obs_dig_hole"],
  },
  blessed_earth: {
    cover: ["obs_forest_shrine", "obs_stone_bench", "obs_birch", "obs_hot_spring", "obs_sapling"],
    litter: ["obs_flower_ring", "obs_flowers", "obs_clover_patch"],
  },
  ley_plains: {
    cover: ["obs_crystal_formation", "obs_toadstool_ring", "obs_crystal_vein", "obs_mushroom"],
    litter: ["obs_glow_moss", "obs_flower_ring"],
  },
  spell_clouds: {
    cover: ["obs_crystal_formation", "obs_ice_floe", "obs_crystal_vein", "obs_frost_bush", "obs_icicle_rock"],
    litter: ["obs_glow_moss", "obs_glacier_crack", "obs_snow_hummock"],
  },
  mirror_pools: {
    cover: ["obs_frozen_pond", "obs_coral", "obs_reed_bed"],
    litter: ["obs_lily_pads", "obs_puddle", "obs_leech_pool"],
  },
  water: {
    cover: ["obs_coral", "obs_ice_floe", "obs_dock_post", "obs_fishing_net", "obs_sunken_stump"],
    litter: ["obs_lily_pads"],
  },
};

const DEFAULT_PALETTE = OBSTACLE_PALETTE["grass"]!;

const paletteFor = (terrain: string): Palette =>
  OBSTACLE_PALETTE[battlefieldKey(terrain).slice("battlefield.".length)] ?? DEFAULT_PALETTE;

/** A cheap integer hash, so neighbouring hexes do not draw the same prop. */
const pick = (list: readonly string[], hex: number): string =>
  list[(Math.imul(hex, 2654435761) >>> 0) % list.length]!;

/**
 * Deterministic **blocking** obstacle sprite for a hex on a given arena. Never
 * litter: a cell a stack may not enter has to look like one.
 */
export function obstacleKey(art: string, terrain: string, hex: number): string {
  if (art.startsWith("obj.")) return art;
  const cover = paletteFor(terrain).cover;
  return `obj.${pick(cover.length > 0 ? cover : DEFAULT_PALETTE.cover, hex)}`;
}

/**
 * Wide pieces: what a formation of two or more blocked cells is drawn as.
 *
 * The classic's obstacles are mostly one painted thing two to five hexes across - a
 * rock ridge, a fallen log, a dry riverbed - and the arena draws a kernel
 * formation as one of these rather than as N copies of a single-hex prop (see
 * `Arena.placeFormation`). Every entry is a 128px-wide sprite from the map
 * object set, chosen low and long. Anything the same arena scatters as litter
 * is filtered out at the call, so a piece that blocks is never also a piece
 * that decorates.
 */
const WIDE_PALETTE: Readonly<Record<string, readonly string[]>> = {
  // Six on the two commonest arenas: a fixture board carries five formations,
  // and with four wide pieces a critic counted the same boulder twice.
  dirt: ["obs_deadfall", "obs_hollow_log", "obs_boulder", "obs_scree", "obs_dry_riverbed", "obs_mesa_rock"],
  grass: ["obs_fallen_branch", "obs_hollow_log", "obs_boulder", "obs_deadfall", "obs_hedgerow", "obs_dry_riverbed"],
  rough: ["obs_mesa_rock", "obs_boulder", "obs_rock_arch", "obs_dry_riverbed"],
  sand: ["obs_dune", "obs_sun_bleached_log", "obs_mesa_rock"],
  snow: ["obs_snow_drift", "obs_glacier_crack", "obs_frozen_pond", "obs_boulder"],
  swamp: ["obs_swamp_log", "obs_bog_pool", "obs_mangrove_root", "obs_reed_bed"],
  lava: ["obs_lava_pool", "obs_lavacrack", "obs_magma_crack", "obs_cinder_cone"],
  cavern: ["obs_cave_pool", "obs_scree", "obs_boulder"],
  wasteland: ["obs_ash_drift", "obs_scree", "obs_sun_bleached_log", "obs_ruined_wall"],
  highlands: ["obs_mountain_ridge", "obs_scree", "obs_boulder"],
  hexed_soil: ["obs_deadfall", "obs_ruined_wall", "obs_boulder"],
  gloom_mist: ["obs_deadfall", "obs_thorn_hedge", "obs_root_arch"],
  ember_fields: ["obs_lavacrack", "obs_ash_drift", "obs_cinder_cone"],
  blessed_earth: ["obs_ruined_arch", "obs_boulder", "obs_fallen_branch"],
  ley_plains: ["obs_boulder", "obs_toadstool_ring", "obs_hollow_log"],
  spell_clouds: ["obs_boulder", "obs_scree"],
  mirror_pools: ["obs_bog_pool", "obs_boulder", "obs_reed_bed"],
  stone_barrens: ["obs_scree", "obs_boulder", "obs_mesa_rock", "obs_rock_arch"],
  lucky_meadow: ["obs_fallen_branch", "obs_hollow_log", "obs_boulder"],
  valor_fields: ["obs_boulder", "obs_fallen_branch", "obs_ruined_wall"],
};
const DEFAULT_WIDE: readonly string[] = ["obs_boulder", "obs_hollow_log", "obs_scree", "obs_fallen_branch"];

/** The pieces a multi-cell formation may be drawn as on this arena. */
export function wideKeys(terrain: string): readonly string[] {
  const id = battlefieldKey(terrain).slice("battlefield.".length);
  const litter = new Set(paletteFor(terrain).litter);
  const list = (WIDE_PALETTE[id] ?? DEFAULT_WIDE).filter((k) => !litter.has(k));
  return list.length > 0 ? list : DEFAULT_WIDE.filter((k) => !litter.has(k));
}

/** The props that may block a cell on this arena. */
export function coverKeys(terrain: string): readonly string[] {
  const cover = paletteFor(terrain).cover;
  return cover.length > 0 ? cover : DEFAULT_PALETTE.cover;
}

/**
 * How bright each arena's blocking props are *as drawn*: the Rec.709 luma of the
 * opaque pixels of that arena's `cover` sprites, in the shipped atlas, averaged
 * over the palette.
 *
 * It is here because it is a property of the palette above and of nothing else,
 * and it exists because a prop's exposure cannot be decided without it. The
 * palettes already carry *some* of the arena's light - lava's five cover sprites
 * average 40.9 against dirt's 82.1 - but not the same share on every arena:
 * cavern's average 84.6, a few per cent off dirt's, on a plate whose ground is
 * 57% of dirt's. `arena.ts` reads this against `groundTones` to work out what is
 * left for the tint to do (`obstacleTint`).
 *
 * Regenerated by `battle-obstacle-tint.spec.ts` straight from
 * `apps/web/public/assets/`, on the same argument as `GROUND_TONES`: a table of
 * measurements that cannot be re-derived is a table of guesses one repack later.
 */
const COVER_LEVEL: Readonly<Record<string, number>> = {
  cavern: 84.6, lucky_meadow: 70.1, hexed_soil: 80.6, dirt: 82.1, gloom_mist: 67.7,
  valor_fields: 75.1, ember_fields: 51.0, grass: 87.8, highlands: 80.9,
  blessed_earth: 104.9, lava: 40.9, mirror_pools: 115.3, spell_clouds: 100.5,
  ley_plains: 71.0, stone_barrens: 91.3, rough: 91.6, sand: 106.7, snow: 119.0,
  swamp: 44.8, wasteland: 56.4, water: 96.3,
};

/**
 * The mean luma of the sprites this arena blocks cells with, resolved through
 * `battlefieldKey` so it cannot disagree with `coverKeys` about which arena it
 * is describing.
 */
export function coverLevel(terrain: string): number {
  const id = battlefieldKey(terrain).slice("battlefield.".length);
  return COVER_LEVEL[id] ?? COVER_LEVEL["grass"]!;
}

/**
 * Sprites scattered flat across the ground so an arena never reads as a stamp.
 * Both halves of the palette: a scatter may put a fallen log on the board as
 * happily as a patch of clover, because nothing about it says "impassable".
 */
export function decalKeys(terrain: string): readonly string[] {
  const p = paletteFor(terrain);
  return [...p.cover, ...p.litter];
}

/** `spell.<school>.<name>` from the def's own art path, which already encodes both. */
export function spellKey(art: string): string {
  const parts = stripExt(art).split("/");
  const base = parts[parts.length - 1] ?? "";
  const school = parts.length >= 2 ? parts[parts.length - 2]! : "all";
  return `spell.${school}.${base.replace(/^\d+_/, "")}`;
}

export const WAR_MACHINE_KEYS: Readonly<Record<string, string>> = {
  ballista: "warmachine.ballista",
  catapult: "warmachine.catapult",
  cannon: "warmachine.cannon",
  supply_wagon: "warmachine.supply_wagon",
  field_hospital: "warmachine.field_hospital",
};
